import { defineStore } from 'pinia';
import apiClient from '@/util/apiClient';

export type SupportCategory = {
	id: number;
	slug: string;
	title: string;
	description?: string | null;
	sortOrder?: number | null;
};

export type SupportArticleListItem = {
	id: number;
	slug: string;
	title: string;
	summary?: string | null;
	categorySlug?: string | null;
	categoryTitle?: string | null;
	sortOrder?: number | null;
	status?: string | null;
	visibility?: string | null;
};

export type SupportArticle = {
	id: number;
	categoryId?: number | null;
	categorySlug?: string | null;
	categoryTitle?: string | null;
	slug: string;
	title: string;
	summary?: string | null;
	markdownBody?: string | null;
	renderedHtml?: string | null;
	status: string;
	visibility: string;
	sortOrder?: number | null;
	publishedOn?: string | null;
	createdOn?: string | null;
	createdBy?: number | null;
	updatedOn?: string | null;
	updatedBy?: number | null;
};

export type SupportCategorySaveRequest = {
	slug: string;
	title: string;
	description?: string | null;
	sortOrder?: number | null;
};

export type SupportArticleSaveRequest = {
	categoryId?: number | null;
	slug: string;
	title: string;
	summary?: string | null;
	markdownBody: string;
	visibility: string;
	sortOrder?: number | null;
};

type SupportDocsState = {
	categories: SupportCategory[];
	articles: SupportArticleListItem[];
	employeeArticles: SupportArticle[];
	currentArticle: SupportArticle | null;

	categoriesLoading: boolean;
	articlesLoading: boolean;
	employeeArticlesLoading: boolean;
	articleLoading: boolean;

	saving: boolean;
	publishing: boolean;
	archiving: boolean;

	errorMessage: string;
};

export type FilePresignResponseFile = {
	fileId: number;
	fileKey: string;
	presignedUrl: string;
	name?: string;
};

export type FilePresignResponse = {
	files: FilePresignResponseFile[];
};

export type FileAssociateRequest = {
	entityType: string;
	entityId: number;
};

function upsertArticle(articles: SupportArticle[], article: SupportArticle): SupportArticle[] {
	const index = articles.findIndex((existing) => existing.id === article.id);

	if (index === -1) {
		return [article, ...articles];
	}

	const next = [...articles];
	next[index] = article;
	return next;
}

export const useSupportDocsStore = defineStore('supportDocs', {
	state: (): SupportDocsState => ({
		categories: [],
		articles: [],
		employeeArticles: [],
		currentArticle: null,

		categoriesLoading: false,
		articlesLoading: false,
		employeeArticlesLoading: false,
		articleLoading: false,

		saving: false,
		publishing: false,
		archiving: false,

		errorMessage: ''
	}),

	getters: {
		hasArticles: (state) => state.articles.length > 0,
		hasEmployeeArticles: (state) => state.employeeArticles.length > 0
	},

	actions: {
		clearError() {
			this.errorMessage = '';
		},
		clearCurrentArticle() {
			this.currentArticle = null;
		},
		async fetchCategories(): Promise<SupportCategory[]> {
			this.categoriesLoading = true;
			this.errorMessage = '';

			try {
				const response = await apiClient.get<SupportCategory[]>('/support/categories/all');
				this.categories = response.data;
				return response.data;
			} catch (error) {
				this.categories = [];
				this.errorMessage = 'Unable to load support categories.';
				throw error;
			} finally {
				this.categoriesLoading = false;
			}
		},
		async fetchArticles(categorySlug?: string | null): Promise<SupportArticleListItem[]> {
			this.articlesLoading = true;
			this.errorMessage = '';

			try {
				const response = await apiClient.get<SupportArticleListItem[]>('/support/articles/all', {
					params: categorySlug ? { category: categorySlug } : {}
				});
				this.articles = response.data;
				return response.data;
			} catch (error) {
				this.articles = [];
				this.errorMessage = 'Unable to load support articles.';
				throw error;
			} finally {
				this.articlesLoading = false;
			}
		},
		async searchArticles(query: string): Promise<SupportArticleListItem[]> {
			const q = query.trim();
			if (!q) {
				return this.fetchArticles();
			}

			this.articlesLoading = true;
			this.errorMessage = '';

			try {
				const response = await apiClient.get<SupportArticleListItem[]>('/support/articles/search', {
					params: { q }
				});
				this.articles = response.data;
				return response.data;
			} catch (error) {
				this.articles = [];
				this.errorMessage = 'Unable to search support articles.';
				throw error;
			} finally {
				this.articlesLoading = false;
			}
		},
		async fetchArticleBySlug(slug: string): Promise<SupportArticle> {
			this.articleLoading = true;
			this.errorMessage = '';
			this.currentArticle = null;

			try {
				const response = await apiClient.get<SupportArticle>(`/support/articles/${slug}`);
				this.currentArticle = response.data;
				return response.data;
			} catch (error) {
				this.currentArticle = null;
				this.errorMessage = 'Unable to load this support article.';
				throw error;
			} finally {
				this.articleLoading = false;
			}
		},
		async fetchAllEmployeeArticles(): Promise<SupportArticle[]> {
			this.employeeArticlesLoading = true;
			this.errorMessage = '';

			try {
				const response = await apiClient.get<SupportArticle[]>('/employee/support/articles/all');
				this.employeeArticles = Array.isArray(response.data) ? response.data : [];
				return this.employeeArticles;
			} catch (error) {
				this.employeeArticles = [];
				this.errorMessage = 'Unable to load support articles.';
				throw error;
			} finally {
				this.employeeArticlesLoading = false;
			}
		},
		async fetchEmployeeArticle(id: number | string): Promise<SupportArticle> {
			this.articleLoading = true;
			this.errorMessage = '';
			this.currentArticle = null;

			try {
				const response = await apiClient.get<SupportArticle>(`/employee/support/articles/${id}`);
				this.currentArticle = response.data;
				return response.data;
			} catch (error) {
				this.currentArticle = null;
				this.errorMessage = 'Unable to load this support article.';
				throw error;
			} finally {
				this.articleLoading = false;
			}
		},
		async createCategory(payload: SupportCategorySaveRequest): Promise<SupportCategory> {
			this.categoriesLoading = true;
			this.errorMessage = '';

			try {
				const response = await apiClient.post<SupportCategory>('/employee/support/categories/save', payload);
				await this.fetchCategories();
				return response.data;
			} catch (error) {
				this.errorMessage = 'Unable to create support category.';
				throw error;
			} finally {
				this.categoriesLoading = false;
			}
		},
		async updateCategory(id: number | string, payload: SupportCategorySaveRequest): Promise<SupportCategory> {
			this.categoriesLoading = true;
			this.errorMessage = '';

			try {
				const response = await apiClient.patch<SupportCategory>(`/employee/support/categories/edit/${id}`, payload);
				await this.fetchCategories();
				return response.data;
			} catch (error) {
				this.errorMessage = 'Unable to update support category.';
				throw error;
			} finally {
				this.categoriesLoading = false;
			}
		},
		async deleteCategory(id: number | string): Promise<void> {
			this.categoriesLoading = true;
			this.errorMessage = '';

			try {
				await apiClient.delete(`/employee/support/categories/delete/${id}`);
				await this.fetchCategories();
			} catch (error) {
				this.errorMessage = 'Unable to delete support category.';
				throw error;
			} finally {
				this.categoriesLoading = false;
			}
		},
		async createEmployeeArticle(payload: SupportArticleSaveRequest): Promise<SupportArticle> {
			this.saving = true;
			this.errorMessage = '';

			try {
				const response = await apiClient.post<SupportArticle>('/employee/support/articles/save', payload);
				this.currentArticle = response.data;
				this.employeeArticles = upsertArticle(this.employeeArticles, response.data);
				return response.data;
			} catch (error) {
				this.errorMessage = 'Unable to create support article.';
				throw error;
			} finally {
				this.saving = false;
			}
		},
		async updateEmployeeArticle(id: number | string, payload: SupportArticleSaveRequest): Promise<SupportArticle> {
			this.saving = true;
			this.errorMessage = '';

			try {
				const response = await apiClient.patch<SupportArticle>(`/employee/support/articles/edit/${id}`, payload);
				this.currentArticle = response.data;
				this.employeeArticles = upsertArticle(this.employeeArticles, response.data);
				return response.data;
			} catch (error) {
				this.errorMessage = 'Unable to save support article.';
				throw error;
			} finally {
				this.saving = false;
			}
		},
		async publishEmployeeArticle(id: number | string): Promise<SupportArticle> {
			this.publishing = true;
			this.errorMessage = '';

			try {
				const response = await apiClient.post<SupportArticle>(`/employee/support/articles/publish/${id}`);
				this.currentArticle = response.data;
				this.employeeArticles = upsertArticle(this.employeeArticles, response.data);
				return response.data;
			} catch (error) {
				this.errorMessage = 'Unable to publish support article.';
				throw error;
			} finally {
				this.publishing = false;
			}
		},
		async archiveEmployeeArticle(id: number | string): Promise<SupportArticle> {
			this.archiving = true;
			this.errorMessage = '';

			try {
				const response = await apiClient.post<SupportArticle>(`/employee/support/articles/archive/${id}`);
				this.currentArticle = response.data;
				this.employeeArticles = upsertArticle(this.employeeArticles, response.data);
				return response.data;
			} catch (error) {
				this.errorMessage = 'Unable to archive support article.';
				throw error;
			} finally {
				this.archiving = false;
			}
		},
		async uploadSupportDocImage(file: File, articleId: number): Promise<number> {
			this.errorMessage = '';

			try {
				const presignResponse = await apiClient.post<FilePresignResponse>('/files/presign', {
					context: 'SUPPORT_DOC',
					files: [
						{
							name: file.name,
							type: file.type,
							size: file.size
						}
					]
				});

				const presignedFile = presignResponse.data.files[0];
				if (!presignedFile) {
					throw new Error('No presigned upload URL returned.');
				}
				await fetch(presignedFile.presignedUrl, {
					method: 'PUT',
					headers: {
						'Content-Type': file.type
					},
					body: file
				});
				await apiClient.post(`/files/${presignedFile.fileId}/associate`, {
					entityType: 'SUPPORT_ARTICLE',
					entityId: articleId
				});
				return presignedFile.fileId;
			} catch (error) {
				this.errorMessage = 'Unable to upload support image.';
				throw error;
			}
		}
	}
});
